import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import MPButtons from './MPButtons'
import MPDescription from './MPDescription'
import SongsList from './SongsList'
import { useUser } from '../../context'
import { Spacer } from '../global'
import { Routines } from '../users/professors/Students/RoutineSection'
import RoutinesAccordion from '../users/professors/Students/RoutineSection/RoutinesAccordion'

import { MusicPlayerContainer } from './styles'

const MPlayer = ({ routines }: { routines: Routines[] }) => {
  const { userRole } = useUser()
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0)
  const [currentRoutine, setCurrentRoutine] = useState<string | null>(null)
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressRef = useRef<HTMLDivElement | null>(null)

  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(300)

  const currentList = openIndex !== null ? routines[openIndex].content : []

  useEffect(() => {
    const audio = audioRef.current

    if (!audio) return

    // Set duration once audio is loaded
    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
    }

    // Update currentTime as audio plays
    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
    }

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [currentTrackIndex])

  const handlePlayPause = () => {
    if (!audioRef.current || !cleanedList[currentTrackIndex]) return

    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleNext = () => {
    if (userRole === 'student') {
      const nextIndex = cleanedList.findIndex(
        (_, i) => i > currentTrackIndex && cleanedList[i].visibility === true,
      )
      if (nextIndex !== -1) {
        setCurrentTrackIndex(nextIndex)
        setCurrentRoutine(cleanedList[nextIndex].routine)
        setIsPlaying(false)
      }
    } else {
      const nextIndex =
        currentTrackIndex < cleanedList.length - 1 ? currentTrackIndex + 1 : 0
      setCurrentTrackIndex(nextIndex)
      setCurrentRoutine(cleanedList[nextIndex].routine)
      setIsPlaying(false)
    }
  }

  const handlePrev = () => {
    if (userRole === 'student') {
      const prevIndex = [...cleanedList]
        .slice(0, currentTrackIndex)
        .reverse()
        .findIndex((audio) => audio.visibility === true)

      if (prevIndex !== -1) {
        const realIndex = currentTrackIndex - 1 - prevIndex
        setCurrentTrackIndex(realIndex)
        setCurrentRoutine(cleanedList[realIndex].routine)
        setIsPlaying(false)
      }
    } else {
      const prevIndex =
        currentTrackIndex > 0 ? currentTrackIndex - 1 : cleanedList.length - 1
      setCurrentTrackIndex(prevIndex)
      setCurrentRoutine(cleanedList[prevIndex].routine)
      setIsPlaying(false)
    }
  }

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const progressBar = progressRef.current
    const audio = audioRef.current
    if (!progressBar || !audio) return

    const rect = progressBar.getBoundingClientRect()
    const clickPosition = e.clientX - rect.left
    const newTime = (clickPosition / rect.width) * duration
    audio.currentTime = newTime
    setCurrentTime(newTime)
  }

  const handleSelectTrack = (index: number, routine: string) => {
    setCurrentTrackIndex(index)
    setCurrentRoutine(routine)
    handlePlayPause()
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
  }

  const cleanedList = useMemo(() => {
    return Object.values(
      currentList.reduce(
        (acc, item) => {
          const key = `${item.routine}-${item.title}`
          if (!acc[key]) acc[key] = item
          return acc
        },
        {} as Record<string, (typeof currentList)[number]>,
      ),
    )
  }, [currentList])

  useEffect(() => {
    if (formatTime(currentTime) === formatTime(duration)) {
      setIsPlaying(false)
    }
  }, [currentTime, duration])

  const handleToggleAccordion = useCallback(
    (index: number) => {
      setOpenIndex(openIndex === index ? null : index)
      setCurrentTrackIndex(0)
      setCurrentRoutine(routines[index].content[0]?.routine || null)
      setIsPlaying(false)
    },
    [openIndex, routines],
  )

  return (
    <>
      <MusicPlayerContainer
        style={{ padding: '30px 0 0px 0', alignItems: 'center', gap: 0 }}
      >
        <MPDescription
          cleanedList={cleanedList}
          currentTrackIndex={currentTrackIndex}
          audioRef={audioRef}
        />

        <MPButtons
          isPlaying={isPlaying}
          handlePrev={handlePrev}
          handleNext={handleNext}
          handlePlayPause={handlePlayPause}
          formatTime={formatTime}
          currentTime={currentTime}
          duration={duration}
          progressRef={progressRef}
          handleProgressClick={handleProgressClick}
        />
      </MusicPlayerContainer>
      <Spacer height={10} />
      <MusicPlayerContainer
        style={{ padding: '0', alignItems: 'center', gap: 0 }}
      >
        <RoutinesAccordion
          routines={routines}
          handleToggleAccordion={handleToggleAccordion}
          openIndex={openIndex}
        >
          <SongsList
            audiosList={cleanedList}
            handleSelectTrack={handleSelectTrack}
            currentTrackIndex={currentTrackIndex}
            currentRoutine={currentRoutine}
            isPlaying={isPlaying}
            handlePlayPause={handlePlayPause}
          />
        </RoutinesAccordion>
      </MusicPlayerContainer>
    </>
  )
}

export default MPlayer
